import { createReducer, sortListItemIdsFn, isMatchingByFilters } from '../../../utils/state';
import { ListItem } from '../../../models/common/list-item';
import { initialListState } from './list.state';
import { LIST_SET, LIST_MERGE, LIST_ITEM_UNSET, LIST_ITEM_SET, LIST_ITEMS_UNSET_ALL } from '../../action-types';
import { PageMeta } from '@core/models/common/page-meta';

export const listItems = createReducer(initialListState, {
    /**
     * Sets the list ids and paging meta.
     * @param state
     * @param payload
     * @returns {{}}
     */
    [LIST_SET]: (state, { payload }) => {
        let meta: any = null;
        let ids: string[] = [];

        // Define all the selected ids including payload
        const domainEntities: any = { ...payload.entities[payload.domain] };
        let selectedGroups: string[] = [...state.selectedGroups];

        // Check if unselected items belong to a selected group and deselect as required
        if (selectedGroups.length > 0) {
            const selectedGroupRemaining: string[] = payload.result
              .filter((id) => state.selectedIds.indexOf(id) < 0)
              .map((id) => (domainEntities[id] as ListItem).getGroup(state.groupBy))
              .filter((group) => selectedGroups.indexOf(group) >= 0)
              .filter((group, index, a) => a.indexOf(group) === index);

            if (selectedGroupRemaining.length > 0) {
                selectedGroups = selectedGroups.filter((group) => selectedGroupRemaining.indexOf(group) < 0);
            }
        }

        // Sort list items based on entity comparison
        if (payload.result && payload.result.length) {
            ids = payload.result;
            if (state.sortBy && !state.sortDisabled) {
                ids.sort(sortListItemIdsFn(state.sortBy, state.sortDirection, payload.entities[payload.domain]));
            }
        }

        // Define paging meta
        if (payload.meta) {
            meta = Object.assign(new PageMeta(), payload.meta);
        }

        const filteredIds = [...ids];

        return { ...state, ids, filteredIds, meta, selectedGroups};
    },

    /**
     * Merges this list items and updates the pages meta.
     * Will auto select new items if they match a selected group.
     * @param state
     * @param payload
     * @returns {{}}
     */
    [LIST_MERGE]: (state, { payload }, entities?: any) => {
        const existingSelectedGroups: string[] = state.selectedGroups;
        const existingSelectedIds: string[] = state.selectedIds;
        const includeSelectedIds: string[] = [];
        const meta: PageMeta = payload.meta as PageMeta;

        let selectedIds: string[] = [ ...existingSelectedIds ];

        // Validate if the item should be selected by selected groups
        if (payload.entities && payload.result && payload.result.length) {
            if (payload.domain) {
                const domain = payload.domain;

                let hasSelectedGroups = existingSelectedGroups && existingSelectedGroups.length > 0;
                Object.keys(payload.entities[domain]).forEach((key) => {
                    let item: ListItem = payload.entities[domain][key] as ListItem;
                    if (hasSelectedGroups && item.isMatchingGroup(state.groupBy, existingSelectedGroups)) {
                        includeSelectedIds.push(item.id);
                    }
                });
            }
        }

        // Define updated ids and remove duplicates
        const domainEntities: any = { ...entities[payload.domain], ...payload.entities[payload.domain] };

        let ids = state.ids
            .filter((itemId) => payload.result.indexOf(itemId) < 0)
            .concat(payload.result);

        if (state.sortBy && !state.sortDisabled) {
            ids.sort(sortListItemIdsFn(state.sortBy, state.sortDirection, domainEntities));
        }

        // Ensure these items matching groups are selected
        if (includeSelectedIds.length > 0) {
            selectedIds = includeSelectedIds
                .filter((timecardId) => existingSelectedIds.indexOf(timecardId) < 0)
                .concat(existingSelectedIds);
        }

        // TODO: J might have to filter the list based on filterBy when merging
        const filteredIds = [...ids];

        return { ...state, ids, filteredIds, selectedIds, meta};
    },

    /**
     * Unsets / Deletes the item from the ids as required.
     * @param state
     * @param payload
     * @returns {{}}
     */
    [LIST_ITEM_UNSET]: (state, { payload }, entities?: any) => {
        const ids = state.ids.filter((id) => id !== payload.id);
        const selectedIds = state.selectedIds.filter((selectedId) => selectedId !== payload.id);

        // Validate if the item has a selected group and still has matching items
        let selectedGroups = state.selectedGroups.concat();
        if (selectedGroups.length && entities) {
            const item: ListItem = entities[payload.domain][payload.id];
            const itemGroup: string = item.getGroup(state.groupBy);
            if (selectedGroups.indexOf(itemGroup) >= 0) {
                let isGroupMatched = false;
                Object.keys(entities[payload.domain]).forEach((key) => {
                    let tmpItem: ListItem = entities[payload.domain][key] as ListItem;
                    if (tmpItem.isMatchingGroup(state.groupBy, [itemGroup]) && tmpItem.id !== payload.id) {
                        isGroupMatched = true;
                    }
                });

                // Remove selected group from ids as required
                if (!isGroupMatched) {
                    selectedGroups = selectedGroups.filter((selectedGroup) => selectedGroup !== itemGroup);
                }
            }
        }

        const filteredIds = [...ids];
        return { ...state, ...{ ids, filteredIds, selectedIds, selectedGroups } };
    },

    /**
     * Unsets / Deletes all items from the ids as required.
     * @param state
     * @param payload
     * @returns {{}}
     */
    [LIST_ITEMS_UNSET_ALL]: (state, { payload }, entities?: any) => {
        return { ...state, ...{ ids: [], filteredIds: [], selectedIds: [], selectedGroups: [] } };
    },

    /**
     * Adds/updates the item from the ids as required.
     * @param state
     * @param payload
     * @returns {{}}
     */
    [LIST_ITEM_SET]: (state, { payload }, entities?: any) => {
        const item: ListItem = payload.item as ListItem;
        let idAttr: string = 'id';

        if (payload.schema && payload.schema._idAttribute) {
            idAttr = payload.schema._idAttribute
        }

        const itemId: string = item[idAttr];

        // Apply filters to domain object to ensure it still belongs to list
        let isMatching: boolean = true;
        isMatching = isMatchingByFilters(state.filterBy, isMatching, item);

        // Apply selection based on filters and if item belongs to selected group
        const selectedIds = [...state.selectedIds];
        let selectedIdIndex: number = selectedIds.indexOf(itemId);
        if (selectedIdIndex >= 0) {
            if (!isMatching) {
                selectedIds.splice(selectedIdIndex, 1);
            }
        } else {
            const itemGroup: string = item.getGroup(state.groupBy);
            if (state.selectedGroups.indexOf(itemGroup) >= 0 && isMatching) {
                selectedIds.push(itemId);
            }
        }

        // Add to listing as required and sort
        const domainEntities: any = { ...entities[payload.domain] };
        domainEntities[itemId] = item;
        let ids: string[] = [...state.ids];

        const idExists: boolean = ids.includes(itemId);
        if(!isMatching && idExists) {
            ids = state.ids.filter((id) => id !== itemId);
        } else if (isMatching && !idExists) {
            ids.push(itemId);
        }

        if (state.sortBy && !state.sortDisabled) {
            ids.sort(sortListItemIdsFn(state.sortBy, state.sortDirection, domainEntities));
        }

        const filteredIds = [...ids];

        return { ...state, ...{ ids, filteredIds, selectedIds } };
    }
});
